import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n';

const C = {
  it: {
    lastUpdated: 'Ultimo aggiornamento: 4 maggio 2026',
    kicker: 'Informativa sulla privacy',
    title: 'Privacy Policy',
    intro:
      'Questo documento descrive come Outfit Detective raccoglie, utilizza e protegge i dati personali degli utenti, in conformità al Regolamento (UE) 2016/679 (GDPR).',
    s1: {
      title: '1. Titolare del trattamento',
      body: "Il titolare del trattamento è Marius-Bogdan Boureanu, persona fisica che gestisce Outfit Detective come progetto in fase beta da Bra (CN), Italia, raggiungibile all'indirizzo: privacy@outfitdetective.com. In caso di costituzione di una società, questa informativa sarà aggiornata. Per qualsiasi richiesta relativa ai tuoi dati puoi scriverci in qualsiasi momento.",
    },
    s2: {
      title: '2. Dati che raccogliamo',
      intro:
        'Raccogliamo solo i dati strettamente necessari per fornire il servizio. Non ti chiediamo nome, cognome, numero di telefono o dati di pagamento.',
      colData: 'Dato',
      colDesc: 'Utilizzo',
      rows: [
        ['Indirizzo email', 'Autenticazione tramite codice OTP monouso. Non usato per marketing e mai ceduto a terzi.'],
        ['Foto personali', 'Caricate volontariamente per generare anteprime try-on. Usate esclusivamente quando avvii una generazione.'],
        ['Anteprime try-on generate', "Immagini AI generate a partire dalle tue foto. Rimangono private salvo che tu scelga esplicitamente di pubblicarle nel Fit Wall."],
        ['Posizione geografica (opzionale)', 'Città o area impostata nelle preferenze per personalizzare i risultati di shopping. Non rileviamo la tua posizione in modo automatico.'],
        ['Nickname / nome visualizzato (opzionale)', 'Nome con cui appari nel Fit Wall pubblico, solo se scegli di condividere un try-on. Puoi usare un nickname anonimo o il tuo nome reale.'],
        ['Profili seguiti', 'Celebrity e creator che segui, necessari per costruire il tuo feed personale.'],
      ],
    },
    s3: {
      title: '3. Base giuridica del trattamento',
      colPurpose: 'Finalità',
      colBasis: 'Base giuridica',
      colData: 'Dati trattati',
      rows: [
        ['Autenticazione sicura', 'Esecuzione del contratto', 'Email'],
        ['Generazione anteprime try-on', 'Consenso esplicito', 'Foto personali'],
        ['Feed personale', 'Esecuzione del contratto', 'Profili seguiti'],
        ['Shopping personalizzato', 'Consenso', 'Posizione geografica'],
        ['Fit Wall pubblico', 'Consenso esplicito', 'Anteprime try-on, nickname'],
      ],
    },
    s4: {
      title: '4. Elaborazione AI delle foto',
      body: "Quando avvii una generazione try-on, la tua foto e i dati del look selezionato vengono elaborati tramite i nostri sistemi e fornitori tecnici AI, incluso OpenAI. Le foto non vengono usate per addestrare modelli AI tramite l'API, non vengono mostrate ad altri utenti e rimangono associate esclusivamente al tuo account.",
    },
    s5: {
      title: '5. Ricerca prodotti e shopping',
      body: "Per mostrare risultati di shopping utilizziamo servizi di ricerca di terze parti. Le query inviate contengono descrizioni dei capi rilevati (es. «giacca di pelle nera») ma non includono dati personali identificativi.",
    },
    s6: {
      title: '6. Conservazione dei dati',
      colData: 'Dato',
      colRetention: 'Periodo di conservazione',
      rows: [
        ['Email', "Finché l'account è attivo. Quando richiedi la cancellazione, l'account viene sospeso per 15 giorni e poi eliminato definitivamente se non lo riattivi."],
        ['Foto personali', "Finché l'utente le elimina manualmente o cancella l'account."],
        ['Anteprime try-on', "Finché l'utente le elimina manualmente o cancella l'account."],
        ['Preferenze e profilo', "Finché l'account è attivo."],
        ['Profili seguiti', "Finché l'utente li rimuove o cancella l'account."],
      ],
    },
    s7: {
      title: '7. I tuoi diritti',
      intro: 'In base al GDPR hai diritto di:',
      rights: [
        ['Accesso', 'Richiedere una copia dei tuoi dati personali.'],
        ['Rettifica', 'Correggere dati inesatti o incompleti.'],
        ['Cancellazione', 'Eliminare il tuo account e tutti i dati associati dalle impostazioni del profilo.'],
        ['Portabilità', 'Ricevere i tuoi dati in formato strutturato e leggibile da macchina.'],
        ['Opposizione', 'Opporti al trattamento in qualsiasi momento.'],
        ['Revoca del consenso', 'Revocare il consenso prestato senza pregiudizio per i trattamenti già effettuati.'],
        ['Reclamo', 'Presentare reclamo al Garante per la protezione dei dati personali (garanteprivacy.it).'],
      ],
      contact: 'Per esercitare i tuoi diritti scrivi a: privacy@outfitdetective.com',
    },
    s8: {
      title: '8. Cookie e archiviazione locale',
      body: "Non utilizziamo cookie di tracciamento o profilazione di terze parti senza consenso. Il browser conserva nel localStorage token tecnici necessari per login, refresh della sessione, preferenze lingua e funzioni richieste dall'utente; alcune viste usano sessionStorage per cache temporanee e posizione di scorrimento. Se introdurremo strumenti analytics non necessari, saranno attivati solo dopo consenso dedicato.",
    },
    s9: {
      title: '9. Sicurezza',
      body: 'Adottiamo misure tecniche e organizzative adeguate per proteggere i tuoi dati da accesso non autorizzato, perdita o distruzione. Tutte le comunicazioni avvengono tramite HTTPS.',
    },
    s10: {
      title: '10. Modifiche a questa informativa',
      body: "Ci riserviamo il diritto di aggiornare questa Privacy Policy. In caso di modifiche sostanziali ti informeremo via email. La data dell'ultimo aggiornamento è indicata in cima a questa pagina.",
    },
    backLabel: "Torna all'app",
    availableNote: 'Questa informativa è disponibile in 6 lingue.',
    legalNote: 'In caso di discrepanze tra le versioni linguistiche, fa fede la versione inglese.',
  },

  en: {
    lastUpdated: 'Last updated: May 4, 2026',
    kicker: 'Privacy notice',
    title: 'Privacy Policy',
    intro:
      'This document describes how Outfit Detective collects, uses, and protects the personal data of its users, in compliance with Regulation (EU) 2016/679 (GDPR).',
    s1: {
      title: '1. Data Controller',
      body: 'The data controller is Marius-Bogdan Boureanu, a natural person operating Outfit Detective as a beta project from Bra (CN), Italy, reachable at: privacy@outfitdetective.com. If a company is incorporated for the service, this policy will be updated. You can write to us at any time with any request relating to your personal data.',
    },
    s2: {
      title: '2. Data we collect',
      intro:
        'We collect only the data strictly necessary to provide the service. We do not ask for your surname, phone number, or payment details.',
      colData: 'Data',
      colDesc: 'Usage',
      rows: [
        ['Email address', 'Authentication via one-time OTP code. Not used for marketing and never shared with third parties.'],
        ['Personal photos', 'Voluntarily uploaded to generate try-on previews. Used exclusively when you start a generation.'],
        ['Generated try-on previews', 'AI-generated images based on your photos. They remain private unless you explicitly choose to publish them to the Fit Wall.'],
        ['Geographic location (optional)', 'A city or area set in preferences to personalise shopping results. We do not automatically detect your location.'],
        ['Nickname / display name (optional)', 'Name shown on the public Fit Wall if you choose to share a try-on. Your choice: anonymous nickname or real name.'],
        ['Followed profiles', 'Celebrities and creators you follow, needed to build your personal feed.'],
      ],
    },
    s3: {
      title: '3. Legal basis for processing',
      colPurpose: 'Purpose',
      colBasis: 'Legal basis',
      colData: 'Data processed',
      rows: [
        ['Secure authentication', 'Contract performance', 'Email'],
        ['Try-on preview generation', 'Explicit consent', 'Personal photos'],
        ['Personal feed', 'Contract performance', 'Followed profiles'],
        ['Personalised shopping', 'Consent', 'Geographic location'],
        ['Public Fit Wall', 'Explicit consent', 'Try-on previews, nickname'],
      ],
    },
    s4: {
      title: '4. AI processing of photos',
      body: 'When you start a try-on generation, your photo and the selected look data are processed through our AI systems and technical AI providers, including OpenAI. Photos are not used to train AI models through the API, are not shown to other users, and remain associated exclusively with your account.',
    },
    s5: {
      title: '5. Product search and shopping',
      body: 'To show shopping results we use third-party search services. Queries sent contain descriptions of detected items (e.g. "black leather jacket") but do not include personally identifiable data.',
    },
    s6: {
      title: '6. Data retention',
      colData: 'Data',
      colRetention: 'Retention period',
      rows: [
        ['Email', 'While the account is active. When you request deletion, the account is suspended for 15 days and then permanently deleted if you do not reactivate it.'],
        ['Personal photos', 'Until the user manually deletes them or deletes their account.'],
        ['Try-on previews', 'Until the user manually deletes them or deletes their account.'],
        ['Preferences and profile', 'While the account is active.'],
        ['Followed profiles', 'Until the user removes them or deletes their account.'],
      ],
    },
    s7: {
      title: '7. Your rights',
      intro: 'Under the GDPR you have the right to:',
      rights: [
        ['Access', 'Request a copy of your personal data.'],
        ['Rectification', 'Correct inaccurate or incomplete data.'],
        ['Erasure', 'Delete your account and all associated data from your profile settings.'],
        ['Portability', 'Receive your data in a structured, machine-readable format.'],
        ['Objection', 'Object to processing at any time.'],
        ['Withdraw consent', 'Withdraw consent previously given, without prejudice to processing carried out before withdrawal.'],
        ['Complaint', 'Lodge a complaint with your national data protection authority.'],
      ],
      contact: 'To exercise your rights, write to: privacy@outfitdetective.com',
    },
    s8: {
      title: '8. Cookies and local storage',
      body: "We do not use third-party tracking or profiling cookies without consent. The browser stores technical tokens in localStorage for login, session refresh, language preferences, and user-requested features; some views use sessionStorage for temporary caches and scroll position. If we introduce non-essential analytics tools, they will be activated only after dedicated consent.",
    },
    s9: {
      title: '9. Security',
      body: 'We adopt appropriate technical and organisational measures to protect your data from unauthorised access, loss, or destruction. All communications are transmitted via HTTPS.',
    },
    s10: {
      title: '10. Changes to this policy',
      body: 'We reserve the right to update this Privacy Policy. In the event of substantial changes, we will inform you by email. The date of the last update is shown at the top of this page.',
    },
    backLabel: 'Back to the app',
    availableNote: 'This policy is available in 6 languages.',
  },

  de: {
    lastUpdated: 'Zuletzt aktualisiert: 4. Mai 2026',
    kicker: 'Datenschutzhinweis',
    title: 'Datenschutzerklärung',
    intro:
      'Dieses Dokument beschreibt, wie Outfit Detective personenbezogene Daten seiner Nutzer erhebt, verwendet und schützt, in Übereinstimmung mit der Verordnung (EU) 2016/679 (DSGVO).',
    s1: {
      title: '1. Verantwortlicher',
      body: 'Der Verantwortliche ist Marius-Bogdan Boureanu, eine natürliche Person, die Outfit Detective als Beta-Projekt von Bra (CN), Italien, betreibt, erreichbar unter: privacy@outfitdetective.com. Falls für den Dienst eine Gesellschaft gegründet wird, wird diese Richtlinie aktualisiert. Sie können uns jederzeit mit Anfragen zu Ihren Daten kontaktieren.',
    },
    s2: {
      title: '2. Erhobene Daten',
      intro:
        'Wir erheben nur die Daten, die zur Erbringung des Dienstes unbedingt erforderlich sind. Wir fragen nicht nach Nachname, Telefonnummer oder Zahlungsdaten.',
      colData: 'Datum',
      colDesc: 'Verwendung',
      rows: [
        ['E-Mail-Adresse', 'Authentifizierung per Einmal-OTP-Code. Nicht für Marketing verwendet und nie an Dritte weitergegeben.'],
        ['Persönliche Fotos', 'Freiwillig hochgeladen, um Try-on-Vorschauen zu erstellen. Ausschließlich bei der Generierung verwendet.'],
        ['Generierte Try-on-Vorschauen', 'KI-generierte Bilder auf Basis Ihrer Fotos. Diese bleiben privat, es sei denn, Sie veröffentlichen sie ausdrücklich im Fit Wall.'],
        ['Geografischer Standort (optional)', 'Eine in den Einstellungen festgelegte Stadt oder Region zur Personalisierung der Einkaufsergebnisse. Wir erfassen Ihren Standort nicht automatisch.'],
        ['Nickname / Anzeigename (optional)', 'Name, der im öffentlichen Fit Wall angezeigt wird, wenn Sie ein Try-on teilen. Ihre Wahl: anonymer Nickname oder echter Name.'],
        ['Gefolgten Profilen', 'Stars und Creator, denen Sie folgen, zur Erstellung Ihres persönlichen Feeds.'],
      ],
    },
    s3: {
      title: '3. Rechtsgrundlage der Verarbeitung',
      colPurpose: 'Zweck',
      colBasis: 'Rechtsgrundlage',
      colData: 'Verarbeitete Daten',
      rows: [
        ['Sichere Authentifizierung', 'Vertragserfüllung', 'E-Mail'],
        ['Try-on-Vorschau-Generierung', 'Ausdrückliche Einwilligung', 'Persönliche Fotos'],
        ['Persönlicher Feed', 'Vertragserfüllung', 'Gefolgte Profile'],
        ['Personalisiertes Shopping', 'Einwilligung', 'Geografischer Standort'],
        ['Öffentlicher Fit Wall', 'Ausdrückliche Einwilligung', 'Try-on-Vorschauen, Nickname'],
      ],
    },
    s4: {
      title: '4. KI-Verarbeitung von Fotos',
      body: 'Wenn Sie eine Try-on-Generierung starten, werden Ihr Foto und die Daten des ausgewählten Looks über unsere KI-Systeme und technische KI-Anbieter, einschließlich OpenAI, verarbeitet. Fotos werden über die API nicht zum Training von KI-Modellen verwendet, anderen Nutzern nicht angezeigt und bleiben ausschließlich mit Ihrem Konto verknüpft.',
    },
    s5: {
      title: '5. Produktsuche und Shopping',
      body: 'Zur Anzeige von Einkaufsergebnissen nutzen wir Drittanbieter-Suchdienste. Die übermittelten Anfragen enthalten Beschreibungen erkannter Kleidungsstücke (z. B. „schwarze Lederjacke"), jedoch keine personenbezogenen Daten.',
    },
    s6: {
      title: '6. Datenspeicherung',
      colData: 'Datum',
      colRetention: 'Aufbewahrungsdauer',
      rows: [
        ['E-Mail', 'Solange das Konto aktiv ist. Gelöscht innerhalb von 30 Tagen nach Kontolöschung.'],
        ['Persönliche Fotos', 'Bis der Nutzer sie manuell löscht oder das Konto schließt.'],
        ['Try-on-Vorschauen', 'Bis der Nutzer sie manuell löscht oder das Konto schließt.'],
        ['Einstellungen und Profil', 'Solange das Konto aktiv ist.'],
        ['Gefolgte Profile', 'Bis der Nutzer sie entfernt oder das Konto schließt.'],
      ],
    },
    s7: {
      title: '7. Ihre Rechte',
      intro: 'Gemäß DSGVO haben Sie das Recht auf:',
      rights: [
        ['Auskunft', 'Eine Kopie Ihrer personenbezogenen Daten anzufordern.'],
        ['Berichtigung', 'Ungenaue oder unvollständige Daten zu korrigieren.'],
        ['Löschung', 'Ihr Konto und alle zugehörigen Daten in den Profileinstellungen zu löschen.'],
        ['Datenübertragbarkeit', 'Ihre Daten in einem strukturierten, maschinenlesbaren Format zu erhalten.'],
        ['Widerspruch', 'Der Verarbeitung jederzeit zu widersprechen.'],
        ['Widerruf der Einwilligung', 'Eine erteilte Einwilligung ohne Nachteile für die bisherige Verarbeitung zu widerrufen.'],
        ['Beschwerde', 'Eine Beschwerde bei Ihrer nationalen Datenschutzbehörde einzureichen.'],
      ],
      contact: 'Um Ihre Rechte auszuüben, schreiben Sie an: privacy@outfitdetective.com',
    },
    s8: {
      title: '8. Cookies und lokaler Speicher',
      body: 'Wir verwenden ohne Einwilligung keine Tracking- oder Profiling-Cookies von Drittanbietern. Der Browser speichert technische Token im localStorage für Login, Sitzungsaktualisierung, Spracheinstellungen und vom Nutzer gewünschte Funktionen; einige Ansichten verwenden sessionStorage für temporäre Caches und Scroll-Positionen. Wenn wir nicht notwendige Analytics-Tools einführen, werden diese nur nach gesonderter Einwilligung aktiviert.',
    },
    s9: {
      title: '9. Sicherheit',
      body: 'Wir setzen angemessene technische und organisatorische Maßnahmen ein, um Ihre Daten vor unbefugtem Zugriff, Verlust oder Zerstörung zu schützen. Alle Kommunikation erfolgt über HTTPS.',
    },
    s10: {
      title: '10. Änderungen dieser Richtlinie',
      body: 'Wir behalten uns das Recht vor, diese Datenschutzerklärung zu aktualisieren. Bei wesentlichen Änderungen werden wir Sie per E-Mail informieren. Das Datum der letzten Aktualisierung wird oben auf dieser Seite angezeigt.',
    },
    backLabel: 'Zurück zur App',
    availableNote: 'Diese Richtlinie ist in 6 Sprachen verfügbar.',
    legalNote: 'Im Falle von Abweichungen zwischen den Sprachversionen ist die englische Fassung maßgeblich.',
  },

  fr: {
    lastUpdated: 'Dernière mise à jour : 4 mai 2026',
    kicker: 'Avis de confidentialité',
    title: 'Politique de confidentialité',
    intro:
      "Ce document décrit comment Outfit Detective collecte, utilise et protège les données personnelles de ses utilisateurs, conformément au Règlement (UE) 2016/679 (RGPD).",
    s1: {
      title: '1. Responsable du traitement',
      body: "Le responsable du traitement est Marius-Bogdan Boureanu, personne physique exploitant Outfit Detective comme projet bêta depuis Bra (CN), Italie, joignable à : privacy@outfitdetective.com. Si une société est constituée pour le service, cette politique sera mise à jour. Vous pouvez nous écrire à tout moment pour toute demande relative à vos données.",
    },
    s2: {
      title: '2. Données que nous collectons',
      intro:
        "Nous ne collectons que les données strictement nécessaires à la fourniture du service. Nous ne demandons pas votre nom de famille, numéro de téléphone ni coordonnées bancaires.",
      colData: 'Donnée',
      colDesc: 'Utilisation',
      rows: [
        ['Adresse e-mail', "Authentification via code OTP unique. Non utilisée à des fins marketing et jamais transmise à des tiers."],
        ['Photos personnelles', "Téléchargées volontairement pour générer des aperçus try-on. Utilisées exclusivement lors d'une génération."],
        ['Aperçus try-on générés', "Images générées par IA à partir de vos photos. Elles restent privées sauf si vous choisissez explicitement de les publier sur le Fit Wall."],
        ['Localisation géographique (optionnelle)', "Ville ou zone définie dans les préférences pour personnaliser les résultats de shopping. Nous ne détectons pas votre position automatiquement."],
        ['Pseudo / nom affiché (optionnel)', "Nom visible sur le Fit Wall public si vous choisissez de partager un try-on. Votre choix : pseudo anonyme ou vrai nom."],
        ['Profils suivis', "Célébrités et créateurs que vous suivez, nécessaires pour construire votre fil d'actualité personnel."],
      ],
    },
    s3: {
      title: '3. Base légale du traitement',
      colPurpose: 'Finalité',
      colBasis: 'Base légale',
      colData: 'Données traitées',
      rows: [
        ['Authentification sécurisée', 'Exécution du contrat', 'E-mail'],
        ["Génération d'aperçus try-on", 'Consentement explicite', 'Photos personnelles'],
        ["Fil d'actualité personnel", 'Exécution du contrat', 'Profils suivis'],
        ['Shopping personnalisé', 'Consentement', 'Localisation géographique'],
        ['Fit Wall public', 'Consentement explicite', 'Aperçus try-on, pseudo'],
      ],
    },
    s4: {
      title: '4. Traitement IA des photos',
      body: "Lorsque vous démarrez une génération try-on, votre photo et les données du look sélectionné sont traitées via nos systèmes IA et des fournisseurs techniques IA, dont OpenAI. Les photos ne sont pas utilisées pour entraîner des modèles IA via l'API, ne sont pas montrées à d'autres utilisateurs et restent associées exclusivement à votre compte.",
    },
    s5: {
      title: '5. Recherche de produits et shopping',
      body: "Pour afficher des résultats de shopping, nous utilisons des services de recherche tiers. Les requêtes envoyées contiennent des descriptions des vêtements détectés (ex. : « veste en cuir noire ») mais n'incluent aucune donnée personnelle identifiable.",
    },
    s6: {
      title: '6. Conservation des données',
      colData: 'Donnée',
      colRetention: 'Durée de conservation',
      rows: [
        ['E-mail', "Tant que le compte est actif. Supprimé dans les 30 jours suivant la suppression du compte."],
        ['Photos personnelles', "Jusqu'à ce que l'utilisateur les supprime manuellement ou supprime son compte."],
        ['Aperçus try-on', "Jusqu'à ce que l'utilisateur les supprime manuellement ou supprime son compte."],
        ['Préférences et profil', "Tant que le compte est actif."],
        ['Profils suivis', "Jusqu'à ce que l'utilisateur les retire ou supprime son compte."],
      ],
    },
    s7: {
      title: '7. Vos droits',
      intro: 'En vertu du RGPD, vous avez le droit de :',
      rights: [
        ['Accès', 'Demander une copie de vos données personnelles.'],
        ['Rectification', 'Corriger des données inexactes ou incomplètes.'],
        ['Effacement', 'Supprimer votre compte et toutes les données associées depuis les paramètres de votre profil.'],
        ['Portabilité', 'Recevoir vos données dans un format structuré et lisible par machine.'],
        ['Opposition', 'Vous opposer au traitement à tout moment.'],
        ['Retrait du consentement', 'Retirer votre consentement sans préjudice des traitements déjà effectués.'],
        ['Réclamation', 'Déposer une plainte auprès de votre autorité nationale de protection des données.'],
      ],
      contact: 'Pour exercer vos droits, écrivez à : privacy@outfitdetective.com',
    },
    s8: {
      title: '8. Cookies et stockage local',
      body: "Nous n'utilisons pas de cookies de suivi ou de profilage tiers sans consentement. Le navigateur stocke dans le localStorage des jetons techniques pour le login, le rafraîchissement de session, les préférences de langue et les fonctions demandées par l'utilisateur ; certaines vues utilisent sessionStorage pour des caches temporaires et la position de défilement. Si nous ajoutons des outils analytics non essentiels, ils ne seront activés qu'après consentement dédié.",
    },
    s9: {
      title: '9. Sécurité',
      body: "Nous adoptons des mesures techniques et organisationnelles appropriées pour protéger vos données contre tout accès non autorisé, perte ou destruction. Toutes les communications sont transmises via HTTPS.",
    },
    s10: {
      title: '10. Modifications de cette politique',
      body: "Nous nous réservons le droit de mettre à jour cette Politique de confidentialité. En cas de modifications substantielles, nous vous en informerons par e-mail. La date de la dernière mise à jour est indiquée en haut de cette page.",
    },
    backLabel: "Retour à l'appli",
    availableNote: 'Cette politique est disponible en 6 langues.',
    legalNote: "En cas de divergence entre les versions linguistiques, la version anglaise fait foi.",
  },

  ro: {
    lastUpdated: 'Ultima actualizare: 4 mai 2026',
    kicker: 'Notă de confidențialitate',
    title: 'Politica de confidențialitate',
    intro:
      'Acest document descrie modul în care Outfit Detective colectează, utilizează și protejează datele personale ale utilizatorilor săi, în conformitate cu Regulamentul (UE) 2016/679 (GDPR).',
    s1: {
      title: '1. Operatorul de date',
      body: 'Operatorul de date este Marius-Bogdan Boureanu, persoana fizica ce gestioneaza Outfit Detective ca proiect beta din Bra (CN), Italia, contactabil la: privacy@outfitdetective.com. Daca va fi infiintata o societate pentru serviciu, aceasta politica va fi actualizata. Ne puteti scrie oricand pentru orice solicitare legata de datele dumneavoastra.',
    },
    s2: {
      title: '2. Datele pe care le colectăm',
      intro:
        'Colectăm doar datele strict necesare pentru furnizarea serviciului. Nu solicităm numele de familie, numărul de telefon sau datele de plată.',
      colData: 'Date',
      colDesc: 'Utilizare',
      rows: [
        ['Adresă de e-mail', 'Autentificare prin cod OTP de unică folosință. Nu este utilizată pentru marketing și nu este transmisă terților.'],
        ['Fotografii personale', 'Încărcate voluntar pentru a genera previzualizări try-on. Utilizate exclusiv la inițierea unei generări.'],
        ['Previzualizări try-on generate', 'Imagini generate de IA pe baza fotografiilor dvs. Rămân private dacă nu alegeți explicit să le publicați pe Fit Wall.'],
        ['Locație geografică (opțional)', 'Un oraș sau zonă setată în preferințe pentru personalizarea rezultatelor de shopping. Nu detectăm automat locația dvs.'],
        ['Pseudonim / nume afișat (opțional)', 'Numele vizibil pe Fit Wall-ul public dacă alegeți să împărtășiți un try-on. Alegerea dvs.: pseudonim anonim sau nume real.'],
        ['Profiluri urmărite', 'Celebrități și creatori pe care îi urmăriți, necesari pentru construirea feed-ului dvs. personal.'],
      ],
    },
    s3: {
      title: '3. Temeiul juridic al prelucrării',
      colPurpose: 'Scop',
      colBasis: 'Temei juridic',
      colData: 'Date prelucrate',
      rows: [
        ['Autentificare securizată', 'Executarea contractului', 'E-mail'],
        ['Generare previzualizări try-on', 'Consimțământ explicit', 'Fotografii personale'],
        ['Feed personal', 'Executarea contractului', 'Profiluri urmărite'],
        ['Shopping personalizat', 'Consimțământ', 'Locație geografică'],
        ['Fit Wall public', 'Consimțământ explicit', 'Previzualizări try-on, pseudonim'],
      ],
    },
    s4: {
      title: '4. Prelucrarea AI a fotografiilor',
      body: 'Cand initiati o generare try-on, fotografia dvs. si datele look-ului selectat sunt prelucrate prin sistemele noastre AI si furnizori tehnici AI, inclusiv OpenAI. Fotografiile nu sunt utilizate pentru antrenarea modelelor AI prin API, nu sunt afisate altor utilizatori si raman asociate exclusiv contului dvs.',
    },
    s5: {
      title: '5. Căutarea produselor și shopping',
      body: 'Pentru a afișa rezultate de shopping, utilizăm servicii de căutare terțe. Interogările trimise conțin descrieri ale articolelor detectate (ex.: „jachetă din piele neagră"), dar nu includ date personale identificabile.',
    },
    s6: {
      title: '6. Păstrarea datelor',
      colData: 'Date',
      colRetention: 'Perioadă de păstrare',
      rows: [
        ['E-mail', 'Cât timp contul este activ. Șterse în 30 de zile de la ștergerea contului.'],
        ['Fotografii personale', 'Până când utilizatorul le șterge manual sau își închide contul.'],
        ['Previzualizări try-on', 'Până când utilizatorul le șterge manual sau își închide contul.'],
        ['Preferințe și profil', 'Cât timp contul este activ.'],
        ['Profiluri urmărite', 'Până când utilizatorul le elimină sau își închide contul.'],
      ],
    },
    s7: {
      title: '7. Drepturile dvs.',
      intro: 'În conformitate cu GDPR, aveți dreptul la:',
      rights: [
        ['Acces', 'Solicitarea unei copii a datelor dvs. personale.'],
        ['Rectificare', 'Corectarea datelor inexacte sau incomplete.'],
        ['Ștergere', 'Ștergerea contului și a tuturor datelor asociate din setările profilului.'],
        ['Portabilitate', 'Primirea datelor dvs. într-un format structurat, citibil automat.'],
        ['Opoziție', 'Obiecția față de prelucrare în orice moment.'],
        ['Retragerea consimțământului', 'Retragerea consimțământului acordat, fără a prejudicia prelucrările deja efectuate.'],
        ['Plângere', 'Depunerea unei plângeri la autoritatea națională de protecție a datelor.'],
      ],
      contact: 'Pentru exercitarea drepturilor dvs., scrieți la: privacy@outfitdetective.com',
    },
    s8: {
      title: '8. Cookie-uri și stocare locală',
      body: 'Nu utilizam cookie-uri de urmarire sau de profilare terte fara consimtamant. Browserul stocheaza in localStorage tokenuri tehnice pentru login, refresh de sesiune, preferinte de limba si functionalitati cerute de utilizator; unele pagini folosesc sessionStorage pentru cache temporar si pozitia de scroll. Daca introducem instrumente analytics neesentiale, acestea vor fi activate doar dupa consimtamant dedicat.',
    },
    s9: {
      title: '9. Securitate',
      body: 'Adoptăm măsuri tehnice și organizatorice adecvate pentru a proteja datele dvs. împotriva accesului neautorizat, pierderii sau distrugerii. Toate comunicările se transmit prin HTTPS.',
    },
    s10: {
      title: '10. Modificări ale acestei politici',
      body: 'Ne rezervăm dreptul de a actualiza această Politică de confidențialitate. În cazul unor modificări substanțiale, vă vom informa prin e-mail. Data ultimei actualizări este indicată în partea de sus a acestei pagini.',
    },
    backLabel: 'Înapoi la aplicație',
    availableNote: 'Această politică este disponibilă în 6 limbi.',
    legalNote: 'În caz de discrepanțe între versiunile lingvistice, versiunea în engleză este cea oficială.',
  },

  es: {
    lastUpdated: 'Última actualización: 4 de mayo de 2026',
    kicker: 'Aviso de privacidad',
    title: 'Política de privacidad',
    intro:
      'Este documento describe cómo Outfit Detective recopila, utiliza y protege los datos personales de sus usuarios, de conformidad con el Reglamento (UE) 2016/679 (RGPD).',
    s1: {
      title: '1. Responsable del tratamiento',
      body: 'El responsable del tratamiento es Marius-Bogdan Boureanu, persona física que gestiona Outfit Detective como proyecto beta desde Bra (CN), Italia, contactable en: privacy@outfitdetective.com. Si se constituye una sociedad para el servicio, esta política se actualizará. Puede escribirnos en cualquier momento para cualquier solicitud relacionada con sus datos.',
    },
    s2: {
      title: '2. Datos que recopilamos',
      intro:
        'Recopilamos solo los datos estrictamente necesarios para prestar el servicio. No solicitamos apellido, número de teléfono ni datos de pago.',
      colData: 'Dato',
      colDesc: 'Uso',
      rows: [
        ['Dirección de correo electrónico', 'Autenticación mediante código OTP de un solo uso. No se utiliza para marketing y nunca se comparte con terceros.'],
        ['Fotos personales', 'Subidas voluntariamente para generar vistas previas de try-on. Se usan exclusivamente al iniciar una generación.'],
        ['Vistas previas try-on generadas', 'Imágenes generadas por IA a partir de sus fotos. Permanecen privadas a menos que elija publicarlas explícitamente en el Fit Wall.'],
        ['Ubicación geográfica (opcional)', 'Ciudad o zona establecida en las preferencias para personalizar los resultados de compra. No detectamos su ubicación automáticamente.'],
        ['Apodo / nombre visible (opcional)', 'Nombre que aparece en el Fit Wall público si elige compartir un try-on. Su elección: apodo anónimo o nombre real.'],
        ['Perfiles seguidos', 'Celebrities y creadores que sigue, necesarios para construir su feed personal.'],
      ],
    },
    s3: {
      title: '3. Base legal del tratamiento',
      colPurpose: 'Finalidad',
      colBasis: 'Base legal',
      colData: 'Datos tratados',
      rows: [
        ['Autenticación segura', 'Ejecución del contrato', 'Correo electrónico'],
        ['Generación de vistas try-on', 'Consentimiento explícito', 'Fotos personales'],
        ['Feed personal', 'Ejecución del contrato', 'Perfiles seguidos'],
        ['Compra personalizada', 'Consentimiento', 'Ubicación geográfica'],
        ['Fit Wall público', 'Consentimiento explícito', 'Vistas try-on, apodo'],
      ],
    },
    s4: {
      title: '4. Tratamiento de fotos por IA',
      body: 'Cuando inicia una generación try-on, su foto y los datos del look seleccionado se procesan mediante nuestros sistemas de IA y proveedores técnicos de IA, incluido OpenAI. Las fotos no se utilizan para entrenar modelos de IA mediante la API, no se muestran a otros usuarios y permanecen asociadas exclusivamente a su cuenta.',
    },
    s5: {
      title: '5. Búsqueda de productos y compras',
      body: 'Para mostrar resultados de compra utilizamos servicios de búsqueda de terceros. Las consultas enviadas contienen descripciones de las prendas detectadas (p. ej., «chaqueta de cuero negra») pero no incluyen datos personales identificables.',
    },
    s6: {
      title: '6. Conservación de datos',
      colData: 'Dato',
      colRetention: 'Período de conservación',
      rows: [
        ['Correo electrónico', 'Mientras la cuenta esté activa. Eliminado en 30 días tras la eliminación de la cuenta.'],
        ['Fotos personales', 'Hasta que el usuario las elimine manualmente o elimine la cuenta.'],
        ['Vistas previas try-on', 'Hasta que el usuario las elimine manualmente o elimine la cuenta.'],
        ['Preferencias y perfil', 'Mientras la cuenta esté activa.'],
        ['Perfiles seguidos', 'Hasta que el usuario los elimine o elimine la cuenta.'],
      ],
    },
    s7: {
      title: '7. Sus derechos',
      intro: 'De conformidad con el RGPD, usted tiene derecho a:',
      rights: [
        ['Acceso', 'Solicitar una copia de sus datos personales.'],
        ['Rectificación', 'Corregir datos inexactos o incompletos.'],
        ['Supresión', 'Eliminar su cuenta y todos los datos asociados desde la configuración del perfil.'],
        ['Portabilidad', 'Recibir sus datos en un formato estructurado y legible por máquina.'],
        ['Oposición', 'Oponerse al tratamiento en cualquier momento.'],
        ['Retirada del consentimiento', 'Retirar el consentimiento prestado sin perjuicio de los tratamientos ya realizados.'],
        ['Reclamación', 'Presentar una reclamación ante su autoridad nacional de protección de datos.'],
      ],
      contact: 'Para ejercer sus derechos, escriba a: privacy@outfitdetective.com',
    },
    s8: {
      title: '8. Cookies y almacenamiento local',
      body: 'No utilizamos cookies de seguimiento o perfilado de terceros sin consentimiento. El navegador almacena en localStorage tokens técnicos para login, actualización de sesión, preferencias de idioma y funciones solicitadas por el usuario; algunas vistas usan sessionStorage para cachés temporales y posición de scroll. Si incorporamos herramientas analytics no esenciales, se activarán solo después de un consentimiento dedicado.',
    },
    s9: {
      title: '9. Seguridad',
      body: 'Adoptamos medidas técnicas y organizativas adecuadas para proteger sus datos contra accesos no autorizados, pérdidas o destrucción. Todas las comunicaciones se transmiten a través de HTTPS.',
    },
    s10: {
      title: '10. Cambios en esta política',
      body: 'Nos reservamos el derecho de actualizar esta Política de privacidad. En caso de cambios sustanciales, le informaremos por correo electrónico. La fecha de la última actualización se muestra en la parte superior de esta página.',
    },
    backLabel: 'Volver a la app',
    availableNote: 'Esta política está disponible en 6 idiomas.',
    legalNote: 'En caso de discrepancia entre las versiones lingüísticas, prevalecerá la versión en inglés.',
  },
};

function PrivacyBody({ c }) {
  return (
    <main className="ct-legal-main">
      <article className="ct-legal-doc">
          <div className="ct-legal-head">
            <p className="ct-legal-kicker">{c.kicker}</p>
            <h1>{c.title}</h1>
            <p className="ct-legal-meta">{c.lastUpdated}</p>
            <p className="ct-legal-intro-text">{c.intro}</p>
            <p className="ct-legal-available">{c.availableNote}</p>
          </div>

          <section className="ct-legal-section">
            <h2>{c.s1.title}</h2>
            <p>{c.s1.body}</p>
          </section>

          <section className="ct-legal-section">
            <h2>{c.s2.title}</h2>
            <p>{c.s2.intro}</p>
            <div className="ct-legal-table-wrap">
              <table className="ct-legal-table">
                <thead>
                  <tr>
                    <th>{c.s2.colData}</th>
                    <th>{c.s2.colDesc}</th>
                  </tr>
                </thead>
                <tbody>
                  {c.s2.rows.map(([data, desc]) => (
                    <tr key={data}>
                      <td><strong>{data}</strong></td>
                      <td>{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="ct-legal-section">
            <h2>{c.s3.title}</h2>
            <div className="ct-legal-table-wrap">
              <table className="ct-legal-table">
                <thead>
                  <tr>
                    <th>{c.s3.colPurpose}</th>
                    <th>{c.s3.colBasis}</th>
                    <th>{c.s3.colData}</th>
                  </tr>
                </thead>
                <tbody>
                  {c.s3.rows.map(([purpose, basis, data]) => (
                    <tr key={purpose}>
                      <td>{purpose}</td>
                      <td><span className="ct-legal-badge">{basis}</span></td>
                      <td>{data}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="ct-legal-section ct-legal-highlight">
            <h2>{c.s4.title}</h2>
            <p>{c.s4.body}</p>
          </section>

          <section className="ct-legal-section">
            <h2>{c.s5.title}</h2>
            <p>{c.s5.body}</p>
          </section>

          <section className="ct-legal-section">
            <h2>{c.s6.title}</h2>
            <div className="ct-legal-table-wrap">
              <table className="ct-legal-table">
                <thead>
                  <tr>
                    <th>{c.s6.colData}</th>
                    <th>{c.s6.colRetention}</th>
                  </tr>
                </thead>
                <tbody>
                  {c.s6.rows.map(([data, retention]) => (
                    <tr key={data}>
                      <td><strong>{data}</strong></td>
                      <td>{retention}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="ct-legal-section">
            <h2>{c.s7.title}</h2>
            <p>{c.s7.intro}</p>
            <ul className="ct-legal-rights">
              {c.s7.rights.map(([right, desc]) => (
                <li key={right}>
                  <strong>{right}</strong>
                  <span>{desc}</span>
                </li>
              ))}
            </ul>
            <p className="ct-legal-contact">
              <i className="ph ph-envelope-simple"></i>
              <span>{c.s7.contact}</span>
            </p>
          </section>

          <section className="ct-legal-section">
            <h2>{c.s8.title}</h2>
            <p>{c.s8.body}</p>
          </section>

          <section className="ct-legal-section">
            <h2>{c.s9.title}</h2>
            <p>{c.s9.body}</p>
          </section>

          <section className="ct-legal-section">
            <h2>{c.s10.title}</h2>
            <p>{c.s10.body}</p>
          </section>

          {c.legalNote ? (
            <p className="ct-legal-official-note">{c.legalNote}</p>
          ) : null}
        </article>
      </main>
  );
}

export default function Privacy({ compact = false }) {
  const { language, setLanguage, languages } = useI18n();
  const lang = C[language] ? language : 'en';
  const c = C[lang];
  const currentLang = languages.find((l) => l.code === language) || languages[0];
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  useEffect(() => {
    function handlePointerDown(event) {
      if (!langRef.current?.contains(event.target)) setLangOpen(false);
    }
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  if (compact) {
    return <PrivacyBody c={c} />;
  }

  return (
    <div className="ct-legal-page">
      <header className="ct-legal-header">
        <Link to="/" className="ct-legal-brand">
          <span className="ct-brand-mark image">
            <img src="/brand/outfit-detective-mark-transparent.svg" alt="" />
          </span>
          <span>Outfit Detective</span>
        </Link>
        <div className="ct-language-menu" ref={langRef}>
          <button
            type="button"
            className="ct-language-trigger"
            onClick={() => setLangOpen((v) => !v)}
            aria-label={currentLang.label}
            aria-expanded={langOpen}
          >
            <span aria-hidden="true">{currentLang.flag}</span>
            <i className="ph ph-caret-down"></i>
          </button>
          {langOpen ? (
            <div className="ct-language-options" role="menu">
              {languages.map((item) => (
                <button
                  key={item.code}
                  type="button"
                  role="menuitem"
                  className={item.code === language ? 'active' : ''}
                  onClick={() => { setLanguage(item.code); setLangOpen(false); }}
                >
                  <span aria-hidden="true">{item.flag}</span>
                  <strong>{item.label}</strong>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </header>
      <PrivacyBody c={c} />
      <footer className="ct-legal-footer">
        <Link to="/" className="ct-legal-back">
          <i className="ph ph-arrow-left"></i>
          {c.backLabel}
        </Link>
        <span>© 2026 Outfit Detective</span>
      </footer>
    </div>
  );
}
